/** Admin endpoints — users, orgs, recruiters, wallets. */
import client from './client';

export const adminAPI = {
  getStats: async () => (await client.get('/admin/stats')).data,
  getUsers: async (page = 1, limit = 20, role = '', search = '') => {
    const q = new URLSearchParams();
    q.append('page', String(page));
    q.append('limit', String(limit));
    if (role) q.append('role', role);
    if (search) q.append('search', search);
    return (await client.get(`/admin/users?${q.toString()}`)).data;
  },
  updateUser: async (id, data) => (await client.put(`/admin/users/${id}`, data)).data,
  deleteUser: async (id, hard = false) => (await client.delete(`/admin/users/${id}${hard ? '?hard=true' : ''}`)).data,
  approveOrg: async (orgId, approved) => (await client.put(`/admin/organizations/${orgId}/approve`, { approved })).data,
  getOrgHierarchy: async () => (await client.get('/admin/orgs/hierarchy')).data,
  revokeOrgDomain: async (orgId) => (await client.post(`/admin/orgs/${orgId}/revoke-domain`)).data,
  restoreOrgDomain: async (orgId) => (await client.post(`/admin/orgs/${orgId}/restore-domain`)).data,
  approveRecruiter: async (userId) => (await client.post(`/admin/recruiters/${userId}/approve`)).data,
  revokeRecruiter: async (userId) => (await client.post(`/admin/recruiters/${userId}/revoke`)).data,
  restoreRecruiter: async (userId) => (await client.post(`/admin/recruiters/${userId}/restore`)).data,
  setOrgHead: async (orgId, userId) => (await client.post(`/admin/orgs/${orgId}/set-head`, { user_id: userId })).data,

  // Feedback (captured by Jay)
  listFeedbacks: async ({ page = 1, limit = 20, status = '', search = '' } = {}) => {
    const q = new URLSearchParams();
    q.append('page', String(page));
    q.append('limit', String(limit));
    if (status) q.append('status', status);
    if (search) q.append('search', search);
    return (await client.get(`/admin/feedbacks?${q.toString()}`)).data;
  },
  getFeedback: async (id) => (await client.get(`/admin/feedbacks/${id}`)).data,
  updateFeedbackStatus: async (id, status) => (await client.patch(`/admin/feedbacks/${id}`, { status })).data,

  // LLM router
  getLLMStatus: async () => (await client.get('/admin/llm/status')).data,
  resetLLMCooldown: async (provider) => {
    const q = provider ? `?provider=${encodeURIComponent(provider)}` : '';
    return (await client.post(`/admin/llm/reset-cooldown${q}`)).data;
  },
  testLLM: async (provider) => (await client.post('/admin/llm/test', provider ? { provider } : {})).data,
  setLLMSticky: async (provider) => (await client.post('/admin/llm/sticky', { provider: provider || null })).data,

  // Jobs (admin-wide control)
  listJobs: async ({ page = 1, limit = 25, status = '', organization_id = '', search = '' } = {}) => {
    const q = new URLSearchParams();
    q.append('page', String(page));
    q.append('limit', String(limit));
    if (status) q.append('status', status);
    if (organization_id) q.append('organization_id', organization_id);
    if (search) q.append('search', search);
    return (await client.get(`/admin/jobs?${q.toString()}`)).data;
  },
  getJob: async (id) => (await client.get(`/admin/jobs/${id}`)).data,
  setJobStatus: async (id, status, reason = '') =>
    (await client.patch(`/admin/jobs/${id}/status`, { status, reason })).data,
  bulkJobAction: async ({ job_ids, action, status = '', reason = '' }) =>
    (await client.post('/admin/jobs/bulk', { job_ids, action, status, reason })).data,

  // WhatsApp Business (Meta Cloud API)
  getWhatsAppStatus: async () => (await client.get('/whatsapp/admin/status')).data,
  listWhatsAppConversations: async ({ page = 1, limit = 20 } = {}) =>
    (await client.get(`/whatsapp/admin/conversations?page=${page}&limit=${limit}`)).data,
  getWhatsAppMessages: async (phone) => (await client.get(`/whatsapp/admin/conversations/${phone}/messages`)).data,
  getWhatsAppTemplates: async () => (await client.get('/whatsapp/admin/templates')).data,
  sendWhatsAppTest: async (data) => (await client.post('/whatsapp/admin/test-message', data)).data,
};

export default adminAPI;
